/**
 * SplashScreen
 *
 * A self-contained, animated branded overlay shown once after a successful
 * login. It orchestrates a four-phase timeline driven by JS timers:
 *
 *   fade-in  → animate (logo pulse + tagline typing) → hold → fade-out
 *
 * Phase classes ('fade-in' | 'animate' | 'fade-out' | 'complete') are applied
 * from the CSS module via styles[phase]; loading is conveyed by the CSS-driven
 * ring spinning around the logo, so the 'hold' window (timings.progress) simply
 * keeps the overlay visible before the fade-out begins.
 *
 * The component communicates completion through the optional `onComplete`
 * callback, which is guaranteed to fire exactly once across every termination
 * path (natural completion, a single skip, or multiple rapid clicks). All
 * timers are cleared on unmount to avoid leaks or stray state updates.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './SplashScreen.module.css'
import { AnimationPhase, SplashScreenProps } from './types'
import { calculateTimings, clampDuration, getTypedCharCount } from './utils'

/** Tagline rendered with the typing effect (mirrors utils.getTypedCharCount). */
const TAGLINE = 'Hệ thống quản lý bán hàng chuyên nghiệp'

/** Title text shown beneath the logo. */
const TITLE = 'Son Kim Land CRM'

/** Version label pinned near the bottom of the overlay. */
const VERSION = 'v5.1.0.0'

/**
 * Son Kim Land logo served from the Portal public assets directory. The Portal
 * references brand images via the public path (e.g. AppConsts.noImage,
 * Login/ForgotPassword), so we follow the same convention here.
 */
const LOGO_SRC = '/assets/images/logo.png'

/** Typing tick interval (~30 chars/sec → ~33ms per tick). */
const TYPING_INTERVAL_MS = 33

/** Default maximum wait (ms) for app readiness after the timeline elapses. */
const DEFAULT_MAX_READY_WAIT_MS = 5000

const SplashScreen: React.FC<SplashScreenProps> = ({
  onComplete,
  duration = 2500,
  skipable = true,
  isAppReady = true,
  maxReadyWaitMs = DEFAULT_MAX_READY_WAIT_MS,
  onReadyTimeout,
}) => {
  // Clamp the configurable duration to [500, 10000] before deriving timings.
  const clampedDuration = useMemo(() => clampDuration(duration), [duration])
  const timings = useMemo(
    () => calculateTimings(clampedDuration),
    [clampedDuration]
  )

  const [phase, setPhase] = useState<AnimationPhase>('fade-in')
  const [typedText, setTypedText] = useState('')

  // Separate stores for timeout and interval ids so cleanup can clear both.
  const timeoutsRef = useRef<number[]>([])
  const intervalsRef = useRef<number[]>([])
  // Guards: ensure onComplete fires once and fade-out is not restarted.
  const onCompleteCalledRef = useRef(false)
  const fadingOutRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Readiness-gate state (Requirements 2.6, 2.7). `timelineElapsedRef` flips
  // true once the pre-fade timeline finishes; if the app is not yet ready at
  // that point the fade-out is held until readiness or the max-wait timeout.
  // Refs mirror the latest prop/callback values so the mount-only timeline
  // effect and the max-wait timer read current values without re-running.
  const timelineElapsedRef = useRef(false)
  const maxWaitStartedRef = useRef(false)
  const isAppReadyRef = useRef(isAppReady)
  const onReadyTimeoutRef = useRef(onReadyTimeout)
  useEffect(() => {
    isAppReadyRef.current = isAppReady
  }, [isAppReady])
  useEffect(() => {
    onReadyTimeoutRef.current = onReadyTimeout
  }, [onReadyTimeout])

  /** Clear every pending timeout and interval and reset the id stores. */
  const clearAllTimers = useCallback(() => {
    timeoutsRef.current.forEach((id) => window.clearTimeout(id))
    intervalsRef.current.forEach((id) => window.clearInterval(id))
    timeoutsRef.current = []
    intervalsRef.current = []
  }, [])

  /** Invoke onComplete exactly once (no-op if already invoked or absent). */
  const handleComplete = useCallback(() => {
    if (onCompleteCalledRef.current) return
    onCompleteCalledRef.current = true
    onComplete?.()
  }, [onComplete])

  /**
   * Transition to the fade-out phase, cancel all pending timeline timers, and
   * schedule completion after the fade-out duration. Idempotent: a fade-out
   * already in progress ignores further requests (Requirement 3.2).
   */
  const beginFadeOut = useCallback(() => {
    if (fadingOutRef.current) return
    fadingOutRef.current = true
    clearAllTimers()
    setPhase('fade-out')
    const id = window.setTimeout(() => {
      setPhase('complete')
      handleComplete()
    }, timings.fadeOut)
    timeoutsRef.current.push(id)
  }, [clearAllTimers, handleComplete, timings.fadeOut])

  /**
   * Skip handler for overlay clicks/taps. Ignored entirely when skipable is
   * false (Requirement 3.3 / 5.6) or while a fade-out is already running.
   */
  const handleSkip = useCallback(() => {
    if (!skipable || fadingOutRef.current) return
    beginFadeOut()
  }, [skipable, beginFadeOut])

  // Keep a stable ref to the latest beginFadeOut so the mount-only timeline
  // effect can trigger the natural fade-out without re-running on each render.
  const beginFadeOutRef = useRef(beginFadeOut)
  useEffect(() => {
    beginFadeOutRef.current = beginFadeOut
  }, [beginFadeOut])

  // Animation timeline orchestration. Runs once on mount; cleans up on unmount.
  useEffect(() => {
    // Phase 1 (fade-in) is the initial state. After the fade-in window elapses,
    // enter the 'animate' phase and start the tagline typing effect.
    const toAnimate = window.setTimeout(() => {
      setPhase('animate')
      let typedElapsed = 0
      const typingId = window.setInterval(() => {
        typedElapsed += TYPING_INTERVAL_MS
        const count = getTypedCharCount(typedElapsed)
        setTypedText(TAGLINE.slice(0, count))
        if (count >= TAGLINE.length) {
          window.clearInterval(typingId)
        }
      }, TYPING_INTERVAL_MS)
      intervalsRef.current.push(typingId)
    }, timings.fadeIn)
    timeoutsRef.current.push(toAnimate)

    // Natural fade-out once the full pre-fade timeline has elapsed. Gated on
    // app readiness (Requirements 2.6, 2.7): if the app is not ready yet, hold
    // the splash visible and arm a max-wait timer; the readiness watcher effect
    // (or the timeout) below triggers the actual fade-out.
    const toFadeOut = window.setTimeout(() => {
      timelineElapsedRef.current = true

      if (isAppReadyRef.current !== false) {
        beginFadeOutRef.current()
        return
      }

      // App not ready: enter the waiting hold and arm the max-wait timer once.
      if (!maxWaitStartedRef.current) {
        maxWaitStartedRef.current = true
        const maxWaitId = window.setTimeout(() => {
          // Readiness never signalled within the window: surface the timeout
          // (so the parent can show an error/retry) and fade out regardless.
          onReadyTimeoutRef.current?.()
          beginFadeOutRef.current()
        }, Math.max(0, maxReadyWaitMs))
        timeoutsRef.current.push(maxWaitId)
      }
    }, timings.fadeIn + timings.animate + timings.progress)
    timeoutsRef.current.push(toFadeOut)

    return () => {
      clearAllTimers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Readiness watcher (Requirements 2.6, 2.7): once the pre-fade timeline has
  // elapsed and the app signals readiness, begin the held fade-out. No-op until
  // the timeline elapses, while already fading out, or when the app is not yet
  // ready. The max-wait timer (armed above) is cleared by beginFadeOut's
  // clearAllTimers, so readiness and timeout paths cannot both run.
  useEffect(() => {
    if (
      timelineElapsedRef.current &&
      isAppReady !== false &&
      !fadingOutRef.current
    ) {
      beginFadeOut()
    }
  }, [isAppReady, beginFadeOut])

  return (
    <div
      ref={containerRef}
      className={`${styles.overlay} ${styles[phase]}`}
      onClick={handleSkip}
      role="presentation"
      aria-label="Màn hình chào Son Kim Land CRM - nhấn để bỏ qua"
    >
      <div className={styles.content}>
        <div className={styles.logoRing}>
          {/* Loading indicator: a gradient arc spinning around the logo. */}
          <span className={styles.ring} aria-hidden="true" />
          <img src={LOGO_SRC} alt="Son Kim Land" className={styles.logo} />
        </div>
        <h1 className={styles.title}>{TITLE}</h1>
        <p className={styles.tagline}>
          {typedText}
          <span className={styles.cursor}>|</span>
        </p>
      </div>
      <span className={styles.version}>{VERSION}</span>
    </div>
  )
}

export default SplashScreen
